import React, { useState } from 'react';
import { BookOpen, Cpu, BrainCircuit, FileSpreadsheet, Check } from 'lucide-react';

const LAYERS = [
  {
    id: 1,
    tag: 'WHERE WE START',
    title: 'Bookkeeping & Accounting',
    subtitle: 'The Solid Foundation',
    icon: BookOpen,
    description:
      'Before intelligence, the foundation must be right. Forty2 handles your complete day-to-day bookkeeping and accounting — accurately and compliantly.',
    features: [
      'Full bookkeeping from day one',
      'Expense and sales capture via AI',
      'Bank reconciliations and ledger maintenance',
      'GST, TDS, and statutory compliance',
      'CA-backed accuracy at every step',
      'Works with Tally, Zoho Books, QuickBooks',
    ],
    highlight: 'Zero migration friction — works on top of your current accounting software.',
  },
  {
    id: 2,
    tag: 'AUTOMATION LAYER',
    title: 'AI Ingestion & Categorization',
    subtitle: 'Continuous Processing',
    icon: Cpu,
    description:
      'Automated capture and categorization working on top of clean books — catching entries in real-time instead of month-end delays.',
    features: [
      'Voice notes, images, PDFs, bills ingested automatically',
      'Automated transaction categorization & tagging',
      'Real-time anomaly and duplicate entry flagging',
      'Instant leakage and cost inefficiency alerts',
    ],
    highlight: 'Submit bills via WhatsApp or photo. AI categorizes and reconciles in seconds.',
  },
  {
    id: 3,
    tag: 'DECISION ENGINE',
    title: 'Financial Intelligence Layer',
    subtitle: 'Turning Data Into Answers',
    icon: BrainCircuit,
    description:
      'Transform raw data into actionable insights that drive decisions. Understand exactly what makes or loses money.',
    features: [
      'Revenue leakage and cost seepage identification',
      'Product line profitability analysis',
      'Customer profitability ranking',
      'Budget vs actual tracking every month',
    ],
    highlight: 'Identify hidden profit drivers and margin-draining contracts effortlessly.',
  },
  {
    id: 4,
    tag: 'EXECUTIVE VISIBILITY',
    title: 'Dynamic Reporting',
    subtitle: 'Clarity for Growth',
    icon: FileSpreadsheet,
    description:
      'Monthly reports that show you exactly where your business stands — structured so decisions are easy, not effortful.',
    features: [
      'P&L statements with variance analysis',
      'Balance sheet health score & solvency metrics',
      'Receivables aging dashboard & collection actions',
      '60–90 day rolling cash flow projections',
    ],
    highlight: 'Always know your runway and cash trajectory 90 days ahead.',
  },
];

export const LayerOperatingSystem: React.FC = () => {
  const [selectedLayer, setSelectedLayer] = useState(1);
  const current = LAYERS.find((l) => l.id === selectedLayer) || LAYERS[0];

  return (
    <div style={{ marginTop: '40px' }}>
      {/* Layer Step Selector */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '12px',
          marginBottom: '28px',
        }}
      >
        {LAYERS.map((layer) => {
          const Icon = layer.icon;
          const isSelected = selectedLayer === layer.id;
          return (
            <button
              key={layer.id}
              onClick={() => setSelectedLayer(layer.id)}
              style={{
                textAlign: 'left',
                padding: '16px 20px',
                borderRadius: '14px',
                backgroundColor: isSelected ? 'var(--brand-red-glow-subtle)' : 'var(--bg-surface)',
                border: isSelected ? '1px solid var(--border-red)' : '1px solid var(--border-subtle)',
                boxShadow: isSelected ? '0 8px 24px -4px var(--brand-red-glow)' : 'none',
                transition: 'all 0.25s ease',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span
                  className="font-mono"
                  style={{
                    fontSize: '0.75rem',
                    color: isSelected ? 'var(--brand-red)' : 'var(--text-muted)',
                    fontWeight: 700,
                  }}
                >
                  LAYER 0{layer.id}
                </span>
                <Icon size={18} color={isSelected ? 'var(--brand-red)' : 'var(--text-muted)'} />
              </div>
              <h4
                style={{
                  fontSize: '0.98rem',
                  fontWeight: 700,
                  color: isSelected ? 'var(--text-primary)' : 'var(--text-secondary)',
                }}
              >
                {layer.title}
              </h4>
            </button>
          );
        })}
      </div>

      {/* Selected Layer Showcase Card */}
      <div
        className="glass-panel-elevated"
        style={{
          padding: '36px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '36px',
          alignItems: 'center',
          border: '1px solid var(--border-red)',
          backgroundColor: 'var(--bg-card)',
        }}
      >
        <div>
          <span className="badge-pill badge-red" style={{ marginBottom: '14px' }}>
            {current.tag}
          </span>
          <h3 style={{ fontSize: '1.65rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>
            {current.title}
          </h3>
          <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '24px' }}>
            {current.description}
          </p>

          <div
            style={{
              padding: '14px 18px',
              backgroundColor: 'var(--bg-surface)',
              borderRadius: '12px',
              border: '1px solid var(--border-subtle)',
              borderLeft: '4px solid var(--brand-red)',
            }}
          >
            <span style={{ fontSize: '0.88rem', color: 'var(--text-primary)', fontStyle: 'italic' }}>
              💡 {current.highlight}
            </span>
          </div>
        </div>

        {/* Features Checklist */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <h4 style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', fontWeight: 700 }}>
            Key Capabilities & Deliverables
          </h4>

          {current.features.map((feat, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: 'var(--bg-surface)',
                borderRadius: '10px',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 230, 118, 0.15)',
                  border: '1px solid rgba(0, 230, 118, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Check size={14} color="var(--signal-green)" />
              </div>
              <span style={{ fontSize: '0.92rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                {feat}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
